import { useState } from 'react';
import { Plus, Search, Filter, Handshake } from 'lucide-react';
import { useDeals, useUpdateDealStage, type DealFilters, type Deal } from '../hooks/useDeals';
import { usePipelines } from '../hooks/usePipelines';
import { PipelineBoard } from '../components/deals/PipelineBoard';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface DealsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusOptions = ['', 'open', 'won', 'lost'];

export function DealsView({ onNavigate }: DealsViewProps) {
  const { data: pipelines, isLoading: pipelinesLoading } = usePipelines();
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Select first pipeline by default
  const activePipelineId = selectedPipelineId || pipelines?.[0]?.id || '';
  const activePipeline = pipelines?.find((p) => p.id === activePipelineId);

  const filters: DealFilters = {
    pipelineId: activePipelineId || undefined,
    status: statusFilter || undefined,
    search: searchText || undefined,
    limit: 200,
  };

  const { data: dealsData, isLoading: dealsLoading } = useDeals(
    activePipelineId ? filters : undefined,
  );

  const updateStage = useUpdateDealStage();

  function handleDealClick(deal: Deal) {
    onNavigate('deal-detail', { dealId: deal.id });
  }

  function handleMoveStage(dealId: string, newStageId: string) {
    updateStage.mutate({ id: dealId, stageId: newStageId });
  }

  const isLoading = pipelinesLoading || dealsLoading;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Deals</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {dealsData ? `${dealsData.total} deals` : 'Manage your pipeline'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => onNavigate('deal-detail', { dealId: 'new' })}>
          <Plus className="h-4 w-4" />
          New Deal
        </button>
      </div>

      {/* Pipeline Selector + Filters */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          {/* Pipeline dropdown */}
          {pipelines && pipelines.length > 1 && (
            <select
              className="input py-1.5"
              value={activePipelineId}
              onChange={(e) => setSelectedPipelineId(e.target.value)}
            >
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); }}
            className="relative flex-1 max-w-sm"
          >
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search deals..."
              className="input pl-9"
            />
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Status</label>
              <select
                className="input py-1.5"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                {statusOptions.filter(Boolean).map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Board */}
      <div className="p-6">
        {isLoading ? (
          <div className="flex gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-96 w-72 shrink-0 animate-pulse rounded-lg bg-surface-100" />
            ))}
          </div>
        ) : activePipeline && dealsData ? (
          dealsData.deals.length > 0 || activePipeline.stages.length > 0 ? (
            <PipelineBoard
              stages={activePipeline.stages}
              deals={dealsData.deals}
              onDealClick={handleDealClick}
              onMoveStage={handleMoveStage}
            />
          ) : (
            <EmptyState
              icon={Handshake}
              title="No deals yet"
              description="Create your first deal to start tracking your pipeline."
              actionLabel="Add Deal"
              onAction={() => onNavigate('deal-detail', { dealId: 'new' })}
            />
          )
        ) : (
          <EmptyState
            icon={Handshake}
            title="No pipelines configured"
            description="Set up a pipeline in Settings to start tracking deals."
            actionLabel="Go to Settings"
            onAction={() => onNavigate('settings')}
          />
        )}
      </div>
    </div>
  );
}
